<template>
  <div class="game-detail-page">
    <van-nav-bar
      :title="$lang.game_detail_title"
      :border="false"
      fixed
      z-index="99999"
      @click-left="onClickLeft"
    >
      <template #left>
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </template>
    </van-nav-bar>

    <div class="game-detail-content">
      <!-- 游戏信息卡片 -->
      <div class="game-info-card">
        <div class="game-info-top">
          <van-image
            :src="gameInfo.gameIcon"
            width="70"
            radius="10"
            class="game-icon"
          >
            <template v-slot:loading>
              <van-loading size="24" color="#28e07a" />
            </template>
          </van-image>
          <div class="game-info-text">
            <div class="game-name-row">
              <span class="game-name">{{ gameInfo.gameName || '–' }}</span>
              <van-icon
                :name="isFav ? 'star' : 'star-o'"
                size="20"
                color="#fff"
                class="fav-icon"
                @click="toggleFavorite"
              />
            </div>
            <p class="game-meta">
              {{ $lang.game_detail_rtp }}: {{ rtpText }}
            </p>
            <p class="game-meta">
              {{ $lang.game_detail_min_bet }}: {{ minBetText }}
            </p>
          </div>
        </div>
        <div class="play-btn-wrap">
          <van-button type="primary" block class="play-btn" :disabled="!canPlay" @click="onPlay">
            <van-icon name="play-circle-o" size="20" />
            {{ $lang.game_detail_play }}
          </van-button>
        </div>
      </div>

      <div v-if="materialReadError" class="game-detail-read-error">
        {{ materialReadError }}
      </div>

      <!-- 游戏预览 -->
      <div class="section">
        <h3 class="section-title">{{ $lang.game_detail_preview }}</h3>
        <div v-if="previewImages.length > 0" class="preview-scroll">
          <div
            v-for="(img, idx) in previewImages"
            :key="'p-' + idx"
            class="preview-item"
          >
            <img :src="img" width="100%" height="180" />
          </div>
        </div>
        <div v-else class="game-detail-empty-state">游戏素材暂不可用</div>
      </div>

      <!-- 推荐游戏 -->
      <div class="section">
        <h3 class="section-title">{{ $lang.game_detail_recommend }}</h3>
        <div v-if="recommendReadError" class="game-detail-read-error">
          {{ recommendReadError }}
        </div>
        <div class="recommend-grid">
          <div
            v-for="(game, idx) in recommendList"
            :key="'r-' + idx"
            class="recommend-item"
            @click="goToGameDetail(game)"
          >
            <img :src="game.gameIcon" width="100%" class="recommend-cover" />
          </div>
        </div>
      </div>
    </div>

    <login v-model="showPopup" overlay></login>
  </div>
</template>

<script>
import { GetHomeGameList, GetGameMaterialList } from '@/api/common'
import { mapState, mapActions } from 'vuex'
import { isFavoriteGame } from '@/store/favoritesStorage'
import {
  buildLaunchPayload,
  collectGamesFromGroups,
  getVisibleGameGroups,
  launchGame
} from '@/components/home/gameLaunch'
import { isOkResponse, safeContent, safeArray } from '@/views/layout/safeLayout'
import { safeGetStorageItem, safeRemoveStorageItem, safeSessionStorage, safeSetStorageItem } from '@/utils/safeStorage'
import {
  filterDisplayGames,
  gameResponseBody,
  gameUnavailableMessage,
  isHomeGameReadableResponse,
  isUnavailableGameEntry
} from './gameResponse'

const GAME_DETAIL_STORAGE = 'gameDetailPayload'
const HOME_GAME_MARKER_ALIAS_KEYS = [
  'readOnlyMode',
  'read_only_mode',
  'mockMode',
  'mock_mode',
  'falseProof',
  'false_proof',
  'failClosed',
  'fail_closed',
  'placeholder',
  'readUnavailable',
  'read_unavailable',
  'routeUnavailable',
  'route_unavailable',
  'technicalMarker',
  'technical_marker',
  'technicalError',
  'technical_error',
  'technicalException',
  'technical_exception',
  'backendFailure',
  'backend_failure',
  'notConnected',
  'not_connected'
]

function homeGameAliasToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function isActiveHomeGameMarkerAlias(value) {
  const token = homeGameAliasToken(value)
  return value === true ||
    value === 1 ||
    ['true', '1', 'yes', 'on'].includes(token) ||
    token.includes('read') ||
    token.includes('mock') ||
    token.includes('placeholder') ||
    token.includes('fallback') ||
    token.includes('fail_closed') ||
    token.includes('fail-closed') ||
    token.includes('fail closed') ||
    token.includes('failclosed') ||
    token.includes('technicalmarker') ||
    token.includes('technical_marker') ||
    token.includes('backendfailure') ||
    token.includes('backend_failure') ||
    token.includes('未接入') ||
    token.includes('只读')
}

function hasHomeGameMarkerAlias(entry) {
  const item = entry && typeof entry === 'object' && !Array.isArray(entry) ? entry : {}
  return HOME_GAME_MARKER_ALIAS_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(item, key) && isActiveHomeGameMarkerAlias(item[key])
  )
}

function isSafeHomeGameEntry(game) {
  return !!(game && typeof game === 'object' && !isUnavailableGameEntry(game) && !hasHomeGameMarkerAlias(game))
}

function shuffle(arr, count) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a.slice(0, count)
}

export default {
  name: 'GameDetail',
  data() {
    return {
      gameInfo: {
        gid: null,
        gameName: '',
        gameIcon: '',
        rtp: '',
        minBet: '',
        gameCode: ''
      },
      jumpPayload: null,
      launchSource: null,
      gameUnavailable: false,
      recommendList: [],
      previewImages: [],
      materialReadError: '',
      recommendReadError: '',
      showPopup: false
    }
  },
  computed: {
    ...mapState(['favorites']),
    unavailableText() {
      return '不可用'
    },
    rtpText() {
      return this.hasDisplayValue(this.gameInfo.rtp)
        ? this.gameInfo.rtp
        : this.unavailableText
    },
    minBetText() {
      if (!this.hasDisplayValue(this.gameInfo.minBet)) return this.unavailableText
      // 展示用金额符与 TopNav 口径统一(GET_CURRENCYUNIT=CFA;getCurrency 是 ISO 码 XOF,不用于展示)
      const currency = this.GET_CURRENCYUNIT || 'CFA'
      return `${currency} ${this.gameInfo.minBet}`
    },
    isFav() {
      if (!this.gameInfo || !this.gameInfo.gid) return false
      return isFavoriteGame(this.favorites, this.getCurrency, this.gameInfo)
    },
    canPlay() {
      return !this.gameUnavailable && !!this.jumpPayload
    }
  },
  created() {
    this.loadFromStorage()
    this.loadRecommend()
  },
  methods: {
    ...mapActions(['addFavorite', 'removeFavorite']),
    hasDisplayValue(value) {
      return value != null && String(value).trim() !== ''
    },
    buildJumpPayload(data) {
      const source = data && typeof data === 'object' ? data : {}
      const cached =
        source.jumpPayload && typeof source.jumpPayload === 'object'
          ? source.jumpPayload
          : {}
      const launchSource = { ...cached, ...source }
      delete launchSource.jumpPayload
      return buildLaunchPayload(launchSource, {
        isDemo:
          source.isDemo != null
            ? source.isDemo
            : cached.isDemo != null
            ? cached.isDemo
            : source.demo != null
            ? source.demo
            : cached.demo,
        typeCate:
          source.typeCate != null
            ? source.typeCate
            : cached.typeCate != null
            ? cached.typeCate
            : 1,
        gameTab:
          source.gameTab || cached.gameTab || this.$route.query.gameTab,
        typeCode: source.typeCode || cached.typeCode || source.code || cached.code
      })
    },
    applyGameDetailPayload(data) {
      const source = data && typeof data === 'object' ? data : {}
      const launchSource =
        source.launchSource && typeof source.launchSource === 'object'
          ? source.launchSource
          : source
      this.launchSource = launchSource
      this.gameUnavailable = isUnavailableGameEntry(launchSource) || !isSafeHomeGameEntry(launchSource)
      const displaySource = {
        ...launchSource,
        ...source
      }
      this.gameInfo = {
        gid: displaySource.gid,
        gameName: displaySource.gameName || '',
        gameIcon: displaySource.gameIcon || displaySource.icon || '',
        rtp: displaySource.rtp != null ? displaySource.rtp : '',
        minBet: displaySource.minBet != null ? displaySource.minBet : '',
        gameCode: displaySource.gameCode || ''
      }
      this.jumpPayload = this.gameUnavailable ? null : this.buildJumpPayload(this.launchSource)
      this.previewImages = this.gameInfo.gameIcon ? [this.gameInfo.gameIcon] : []
      this.loadGameMaterial()
    },
    loadFromStorage() {
      try {
        const raw = safeGetStorageItem(safeSessionStorage(), GAME_DETAIL_STORAGE)
        if (!raw) {
          this.$jumpTo('/home', {}, { replace: true })
          return
        }
        this.applyGameDetailPayload(JSON.parse(raw))
      } catch (e) {
        this.$jumpTo('/home', {}, { replace: true })
      }
    },
    async loadGameMaterial() {
      if (!this.gameInfo || !this.gameInfo.gid) return
      try {
        const data = await GetGameMaterialList({ gid: this.gameInfo.gid })
        if (isOkResponse(data)) {
          const content = safeContent(data)
          const list = safeArray(content.list || content.dataList)
          const urls = list
            .slice(0, 3)
            .map((item) =>
              typeof item === 'string'
                ? item
                : item.url || item.img || item.image
            )
            .filter(Boolean)
          if (urls.length > 0) {
            this.previewImages = urls
            this.materialReadError = ''
          } else {
            this.showMaterialReadFailure('This feature is temporarily unavailable. Please try again later.')
          }
        } else {
          this.showMaterialReadFailure('This feature is temporarily unavailable. Please try again later.')
        }
      } catch (_) {
        this.showMaterialReadFailure('This feature is temporarily unavailable. Please try again later.')
      }
    },
    showMaterialReadFailure(message) {
      this.materialReadError = message
      this.$toast({
        message,
        icon: 'cross'
      })
    },
    async loadRecommend() {
      try {
        const data = await GetHomeGameList()
        if (isHomeGameReadableResponse(data)) {
          const groups = getVisibleGameGroups(gameResponseBody(data), this.getCurrency)
          const list = filterDisplayGames(collectGamesFromGroups(groups, { dedupe: true }))
            .filter((game) => isSafeHomeGameEntry(game))
          const excludeGid = this.gameInfo.gid
          const others = list.filter((g) => g && g.gid !== excludeGid)
          this.recommendList = shuffle(others, 6)
          this.recommendReadError = ''
        } else {
          this.recommendList = []
          this.recommendReadError = '推荐游戏暂不可用'
        }
      } catch (_) {
        this.recommendList = []
        this.recommendReadError = '推荐游戏加载失败'
      }
    },
    onClickLeft() {
      this.$router.go(-1)
    },
    toggleFavorite() {
      if (!this.token) {
        this.showPopup = true
        return
      }
      if (this.gameUnavailable) {
        this.$toast({
          message: gameUnavailableMessage(this.$lang.common_txt145),
          icon: 'cross'
        })
        return
      }
      if (!this.gameInfo || !this.gameInfo.gid) return
      const item = this.gameInfo
      const payload = { currency: this.getCurrency, item }
      if (this.isFav) {
        this.removeFavorite(payload)
      } else {
        this.addFavorite(payload)
      }
    },
    onPlay() {
      if (!this.token) {
        this.showPopup = true
        return
      }
      if (this.gameUnavailable) {
        this.$toast({
          message: gameUnavailableMessage(this.$lang.common_txt145),
          icon: 'cross'
        })
        return
      }
      const payload = this.buildJumpPayload(this.launchSource)
      this.jumpPayload = payload
      if (!payload) {
        this.$toast({
          message: this.$lang.common_txt145,
          icon: 'cross'
        })
        return
      }
      try {
        safeRemoveStorageItem(safeSessionStorage(), GAME_DETAIL_STORAGE)
      } catch (_) {
        // ignore storage errors
      }
      launchGame(this, payload)
    },
    goToGameDetail(game) {
      if (!game || !game.gid || isUnavailableGameEntry(game) || !isSafeHomeGameEntry(game)) {
        this.$toast({
          message: gameUnavailableMessage(this.$lang.common_txt145),
          icon: 'cross'
        })
        return
      }
      const jumpPayload = buildLaunchPayload(game, {
        isDemo: this.$route.query.isDemo || this.$route.query.demo,
        typeCate: 1,
        gameTab: this.$route.query.gameTab,
        typeCode: game.typeCode || game.code
      })
      if (!jumpPayload) {
        this.$toast({
          message: this.$lang.common_txt145,
          icon: 'cross'
        })
        return
      }
      const payload = {
        ...game,
        launchSource: game,
        jumpPayload
      }
      try {
        safeSetStorageItem(safeSessionStorage(), GAME_DETAIL_STORAGE, JSON.stringify(payload))
      } catch (_) {
        // Storage can fail in private mode; the current page can still update.
      }
      this.applyGameDetailPayload(payload)
      this.loadRecommend()
    }
  }
}
</script>

<style lang="less" scoped>
.game-detail-page {
  min-height: 100vh;
  padding-top: 55px;
  background: #1a1d26;
  padding-bottom: 80px;
}
.game-detail-content {
  padding: 16px 12px 24px;
}
.game-info-card {
  background: #252830;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
}
.game-info-top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.game-icon {
  flex-shrink: 0;
}
.game-info-text {
  flex: 1;
  min-width: 0;
}
.game-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.game-name {
  font-size: var(--vk-fs-md);
  font-weight: 600;
  color: #ffffff;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fav-icon {
  flex-shrink: 0;
  margin-left: 8px;
}
.game-meta {
  font-size: var(--vk-fs-xs);
  color: #9da7bc;
  margin: 4px 0 0;
}
.play-btn-wrap {
  padding-top: 20px;
  width: 100%;
}
.play-btn {
  width: 100%;
  height: 44px;
  background: #28e07a !important;
  border: none !important;
  color: #000 !important;
  font-weight: 600;
  font-size: var(--vk-fs-md);
  border-radius: 10px;
}
.section {
  margin-bottom: 24px;
}
.section-title {
  font-size: var(--vk-fs-15);
  color: #ffffff;
  margin: 0 0 12px;
  font-weight: 600;
}
.game-detail-read-error {
  margin: 8px 0 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(255, 193, 7, 0.12);
  color: #ffd36a;
  font-size: var(--vk-fs-xs);
  line-height: 1.4;
}
.preview-scroll {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}
.preview-item {
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  overflow: hidden;
}
.game-detail-empty-state {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #252830;
  color: #9da7bc;
  font-size: var(--vk-fs-xs);
}
.recommend-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.recommend-item {
  width: 31.5%;
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
}
.recommend-cover {
  display: block;
  ::v-deep .van-image__img {
    border-radius: 10px;
    height: 150px;
    object-fit: cover;
  }
}
</style>
